import {
  HeartIcon,
  ClipboardDocumentListIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  CalculatorIcon,
  FolderIcon,
  PhotoIcon,
  FireIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

export interface NavigationItem {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  descriptionKey?: string;
  releaseQuarter?: string;
  badgeKey?: string;
}

export interface NavigationCategory {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
  defaultExpanded?: boolean;
}

// Custom Pill icon since heroicons doesn't have one
const MedicationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611l-3.773.628a1.5 1.5 0 0 1-1.504-.627l-.628-1.047a1.5 1.5 0 0 0-1.504-.628l-3.773.629c-1.718.286-2.299-2.379-1.067-3.611L5 14.5" />
  </svg>
);

export const navigationCategories: NavigationCategory[] = [
  {
    id: 'general',
    labelKey: 'common:navigation.categories.general',
    icon: HeartIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'health-questions',
        labelKey: 'common:navigation.items.healthQuestions',
        path: '/health-questions',
        icon: QuestionMarkCircleIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.healthQuestions',
      },
      {
        id: 'medical-glossary',
        labelKey: 'common:navigation.items.medicalGlossary',
        path: '/medical-glossary',
        icon: BookOpenIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.medicalGlossary',
      },
      {
        id: 'health-metrics',
        labelKey: 'common:navigation.items.healthMetrics',
        path: '/health-metrics',
        icon: CalculatorIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.healthMetrics',
      },
      {
        id: 'health-records',
        labelKey: 'common:navigation.items.healthRecords',
        path: '/health-records',
        icon: FolderIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.healthRecords',
      },
    ],
  },
  {
    id: 'health-tracking',
    labelKey: 'common:navigation.categories.healthTracking',
    icon: ClipboardDocumentListIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'symptom-tracker',
        labelKey: 'common:navigation.items.symptomTracker',
        path: '/symptom-tracker',
        icon: ClipboardDocumentListIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.symptomTracker',
      },
      {
        id: 'exercise-tracker',
        labelKey: 'common:navigation.items.exerciseTracker',
        path: '/exercise-tracker',
        icon: FireIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.exerciseTracker',
      },
    ],
  },
  {
    id: 'medical-documents',
    labelKey: 'common:navigation.categories.medicalDocuments',
    icon: DocumentDuplicateIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'lab-results',
        labelKey: 'common:navigation.items.labResults',
        path: '/lab-results',
        icon: BeakerIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.labResults',
      },
      {
        id: 'imaging-reports',
        labelKey: 'common:navigation.items.imagingReports',
        path: '/imaging-reports',
        icon: PhotoIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.imagingReports',
      },
      {
        id: 'visit-notes',
        labelKey: 'common:navigation.items.visitNotes',
        path: '/visit-notes',
        icon: DocumentTextIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.visitNotes',
      },
    ],
  },
  {
    id: 'medication',
    labelKey: 'common:navigation.categories.medication',
    icon: MedicationIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'medication-info',
        labelKey: 'common:navigation.items.medicationInfo',
        path: '/medication-info',
        icon: MedicationIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.medicationInfo',
      },
      {
        id: 'interaction-checker',
        labelKey: 'common:navigation.items.interactionChecker',
        path: '/interaction-checker',
        icon: ExclamationTriangleIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.interactionChecker',
      },
      {
        id: 'medication-list',
        labelKey: 'common:navigation.items.medicationList',
        path: '/my-medications',
        icon: ListBulletIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.medicationList',
      },
    ],
  },
  {
    id: 'resources',
    labelKey: 'common:navigation.categories.resources',
    icon: BookOpenIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'insurance-guide',
        labelKey: 'common:navigation.items.insuranceGuide',
        path: '/insurance-guide',
        icon: ShieldCheckIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.insuranceGuide',
      },
      {
        id: 'ai-health-advisor',
        labelKey: 'common:navigation.items.aiHealthAdvisor',
        path: '/ai-advisor',
        icon: ChatBubbleLeftRightIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.aiHealthAdvisor',
      },
    ],
  },
];

// Utility functions
export const getAllNavigationItems = (): NavigationItem[] => {
  return navigationCategories.flatMap((category) => category.items);
};

export const findNavigationItemByPath = (path: string): NavigationItem | undefined => {
  return getAllNavigationItems().find((item) => item.path === path);
};

export const getCategoryForPath = (path: string): NavigationCategory | undefined => {
  return navigationCategories.find((category) =>
    category.items.some((item) => item.path === path)
  );
};
